import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useEnduserStore } from "../store/hooks";

export interface UseIframeTokenOptions {
  /**
   * Whether to fetch the iframe token. Defaults to true.
   * Set to false to disable fetching until needed.
   */
  enabled?: boolean;
}

export interface UseIframeTokenResult {
  /** The iframe token, or null if not available */
  token: string | null;
  /** Whether the token is currently being fetched */
  isLoading: boolean;
  /** Error from token fetch, if any */
  error: Error | null;
  /** Manually refetch the token */
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching one-time iframe authentication tokens.
 *
 * TODO: Once tanstack-query library supports dynamic method classification,
 * refactor to use useTellescopeSessionQuery with refresh_session classified as a query.
 * This will eliminate the need for direct useQuery usage and unique query key generation.
 *
 * Uses TanStack Query's `useQuery` to fetch iframe tokens via `session.refresh_session()`.
 * Tokens are one-time use and the original session token is preserved by saving
 * and restoring it after the refresh.
 *
 * Each hook instance gets a unique query key to ensure independent token fetches
 * (no cross-component deduplication), since tokens are one-time use.
 *
 * @example
 * ```tsx
 * // Basic usage
 * const { token, isLoading, error } = useIframeToken();
 *
 * // Conditional fetching
 * const { token, isLoading } = useIframeToken({ enabled: !!formId });
 * ```
 */
export const useIframeToken = (options: UseIframeTokenOptions = {}): UseIframeTokenResult => {
  const { enabled = true } = options;
  const session = useEnduserStore((state) => state.session);

  // Generate unique ID per hook instance to prevent cross-component deduplication
  // Tokens are one-time use, so each component needs its own token
  const instanceId = useMemo(() => Math.random().toString(36).substring(7), []);

  const shouldFetch = enabled && !!session;

  const { data, isLoading, error, refetch } = useQuery<string | null, Error>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- session.authToken in queryKey is sufficient; full session object would cause unnecessary refetches
    queryKey: ["iframe-token", session?.authToken, instanceId],
    queryFn: async () => {
      if (!session) return null;

      // Save current token before refresh
      const currentToken = session.authToken;

      // Fetch new token via refresh_session
      const refreshResult = await session.refresh_session({
        invalidatePreviousToken: false,
      });

      // Restore original session token (SDK might auto-update it)
      if (currentToken && session.authToken !== currentToken) {
        session.setAuthToken(currentToken);
      }

      return refreshResult.authToken;
    },
    enabled: shouldFetch,
    staleTime: 0, // Always fetch fresh - tokens are one-time use
    gcTime: 0, // Don't cache after unmount
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false, // Don't retry auth failures
  });

  return {
    token: data ?? null,
    isLoading,
    error,
    refetch: async () => {
      const result = await refetch();
      if (result.error) throw result.error;
    },
  };
};
